// Period selection for the telemetry panels. Telemetry timestamps are station
// wall-clock tagged UTC, so every calculation here is done in UTC and the ISO
// strings are sliced, never passed through the browser's local timezone.
import type { OpenFile } from "../types";

export type PeriodKind = "all" | "preset" | "file" | "custom";

export interface PeriodSel {
  kind: PeriodKind;
  from: string | null;   // ISO, first second (null = from the first data)
  to: string | null;     // ISO, LAST second inclusive (null = to the last data)
  preset?: string;       // "7d" | "3d" | "24h" | "12h" | "6h" | "1h" | "lastday"
  file?: OpenFile;
}

export const ALL: PeriodSel = { kind: "all", from: null, to: null };
export const PRESET_HOURS: Record<string, number> = { "7d": 168, "3d": 72, "24h": 24, "12h": 12, "6h": 6, "1h": 1 };

const pad = (n: number) => (n < 10 ? "0" : "") + n;

/** ISO string (any offset) → epoch seconds, treating the wall-clock digits as UTC. */
export function isoToSec(iso: string): number {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return NaN;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? 0)) / 1000;
}

/** epoch seconds → "YYYY-MM-DDTHH:MM:SS+00:00" (station time tagged UTC). */
export function secToIso(s: number): string {
  const d = new Date(s * 1000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}+00:00`;
}

export const secToDate = (s: number) => secToIso(s).slice(0, 10);     // YYYY-MM-DD
export const secToTime = (s: number) => secToIso(s).slice(11, 19);    // HH:MM:SS

/** "28.09.2026 20:28:40" from an ISO string (station time). */
export function fmtDT(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)} ${iso.slice(11, 19)}`;
}
export const fmtD = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
export const fmtT = (iso: string) => iso.slice(11, 19);

/** Table row step (seconds) the PDF will use for a span — mirrors the backend rule. */
export function stepFor(spanSec: number): number {
  if (spanSec <= 180) return 1;   // event windows stay per-second
  for (const s of [1, 60, 300, 900, 3600, 21600, 86400]) if (spanSec / s <= 120) return s;
  return 86400;
}

export function durationText(spanSec: number, t: (k: string, v?: Record<string, string | number>) => string): string {
  if (spanSec < 120) return t("period.dur_s", { n: spanSec });
  if (spanSec < 7200) return t("period.dur_min", { n: Math.round(spanSec / 60) });
  const dec = t("period.decimal");   // "," for ru/fa, "." for en
  const one = (x: number) => (Number.isInteger(x) ? String(x) : x.toFixed(1).replace(".", dec));
  if (spanSec < 172800) return t("period.dur_h", { n: one(spanSec / 3600) });
  return t("period.dur_d", { n: one(spanSec / 86400) });
}

export function stepText(step: number, t: (k: string) => string): string {
  const key = { 1: "sec", 10: "10s", 60: "min", 300: "5min", 900: "15min", 3600: "hour", 21600: "6h", 86400: "day" }[step] ?? "day";
  return t("period.step_" + key);
}

/** Concrete [from, to] seconds for any selection, given the data range (minute buckets). */
export function resolve(sel: PeriodSel, dataFrom: string | null | undefined, dataTo: string | null | undefined): { from: number; to: number } | null {
  if (sel.from && sel.to) return { from: isoToSec(sel.from), to: isoToSec(sel.to) };
  if (!dataFrom || !dataTo) return null;
  // data_to is the last minute bucket → its last second is +59 s
  return { from: isoToSec(dataFrom), to: isoToSec(dataTo) + 59 };
}

/** Build the selection for a quick preset relative to the newest data. */
export function presetSel(preset: string, dataTo: string | null | undefined): PeriodSel {
  if (preset === "all" || !dataTo) return ALL;
  const to = isoToSec(dataTo) + 59;
  if (preset === "lastday") {
    const dayStart = isoToSec(secToDate(to) + "T00:00:00");
    return { kind: "preset", preset, from: secToIso(dayStart), to: secToIso(to) };
  }
  const hours = PRESET_HOURS[preset] ?? 24;
  return { kind: "preset", preset, from: secToIso(to - hours * 3600 + 1), to: secToIso(to) };
}

export function fileSel(f: OpenFile): PeriodSel {
  return { kind: "file", file: f, from: f.from, to: f.to };
}

/** API query window: exclusive end = last second + 1. */
export function apiWindow(sel: PeriodSel): { from: string; to: string } {
  if (!sel.from || !sel.to) return { from: "", to: "" };
  return { from: sel.from, to: secToIso(isoToSec(sel.to) + 1) };
}
